import { ROLE_LABEL } from '../lib/roles'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useTheme } from '../lib/theme'
import { useT } from '../lib/i18n'
import { Mark } from '../components/Logo'
import { cx } from '../components/ui'

interface NavItem {
  to: string
  label: string
}

const NAV: Record<string, NavItem[]> = {
  rep: [
    { to: '/price', label: 'Price finder' },
    { to: '/quotes', label: 'My quotes' },
  ],
  supervisor: [
    { to: '/queue', label: 'Escalations' },
    { to: '/team', label: 'Team' },
    { to: '/price', label: 'Price finder' },
    { to: '/quotes', label: 'Quotes' },
  ],
  head: [
    { to: '/team', label: 'Teams' },
    { to: '/queue', label: 'Below-floor requests' },
  ],
  admin: [
    { to: '/admin/versions', label: 'Model versions' },
    { to: '/admin/uplift', label: 'Uplift' },
    { to: '/admin/governance', label: 'Governance' },
    { to: '/admin/import', label: 'Reference data' },
    { to: '/admin/users', label: 'Users' },
    { to: '/admin/audit', label: 'Audit log' },
  ],
}

const PLATFORM_NAV: NavItem[] = [
  { to: '/platform/tenants', label: 'Tenants' },
  { to: '/platform/operators', label: 'Operators' },
]


/** Top bar in the house style: brand, the tenant as context, tabs, and the user on the right. */
export function Shell() {
  const { user, operator, tenant, logout } = useAuth()
  const { theme, toggle } = useTheme()
  const { t, lang, setLang } = useT()
  if (!user && !operator) return null
  const items = operator ? PLATFORM_NAV : (NAV[user!.role] ?? [])
  const name = operator ? operator.displayName : user!.displayName
  const initials = name
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  const home = items[0]?.to ?? '/'

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-panel shadow-panel">
        <div className="mx-auto flex h-16 max-w-[1440px] items-stretch gap-4 px-6 xl:gap-5 xl:px-8">
          <Link to={home} className="flex items-center gap-2.5">
            <Mark size={26} />
            <span className="serif text-[22px] font-bold leading-none text-sky-500">PricePilot</span>
          </Link>
          <div className="my-3 whitespace-nowrap border-l border-slate-800 pl-4 text-[12px] xl:pl-6 leading-[1.25] text-slate-500">
            <div className="font-semibold text-slate-100">{operator ? t('Platform console') : tenant?.name}</div>
            <div>
              {operator ? t('Platform operator') : t(ROLE_LABEL[user!.role] ?? user!.role)}
              {user?.salesTeam ? ` · ${user.salesTeam}` : ''}
            </div>
          </div>
          <nav className="flex items-stretch xl:ml-2">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cx(
                    'flex items-center whitespace-nowrap border-b-2 px-2.5 text-[14px] font-medium transition xl:px-3.5',
                    isActive ? 'border-sky-500 bg-sky-500/5 text-sky-500' : 'border-transparent text-slate-500 hover:text-slate-100',
                  )
                }
              >
                {t(n.label)}
              </NavLink>
            ))}
          </nav>
          <UserMenu initials={initials} name={name}>
            <MenuItem onClick={() => setLang(lang === 'de' ? 'en' : 'de')}>{lang === 'de' ? 'English' : 'Deutsch'}</MenuItem>
            <MenuItem onClick={toggle}>{theme === 'dark' ? t('Light') : t('Dark')}</MenuItem>
            <div className="my-1 border-t border-slate-800" />
            <MenuItem onClick={() => logout()}>{t('Sign out')}</MenuItem>
          </UserMenu>
        </div>
      </header>

      <main className="min-w-0 flex-1">
        <div className="mx-auto max-w-[1440px] px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

/** The signed-in user on the right of the bar; language, theme and sign-out open beneath it. */
function UserMenu({ initials, name, children }: { initials: string; name: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', esc)
    }
  }, [open])
  return (
    <div ref={ref} className="relative ml-auto flex items-center">
      <button type="button" className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-800/40" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500 text-[11px] font-semibold text-white">{initials}</span>
        <span className="hidden whitespace-nowrap text-[13px] font-medium text-slate-100 xl:block">{name}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" className="text-slate-500" aria-hidden="true">
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 min-w-[12rem] rounded-lg border border-slate-800 bg-panel py-1 shadow-panel" onClick={() => setOpen(false)}>
          <div className="px-3 pb-1.5 pt-1 text-[12px] font-medium text-slate-500">{name}</div>
          {children}
        </div>
      )}
    </div>
  )
}

function MenuItem({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className="block w-full px-3 py-1.5 text-left text-[14px] text-slate-300 hover:bg-slate-800/40 hover:text-slate-100" onClick={onClick}>
      {children}
    </button>
  )
}
